import { ArrowDownNarrowWide, Check, Clock, Minus, X } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { formatSigningDateTime } from "../format";
import { chainSteps, currentPosition, isOrderedChain, signingMethodKey } from "../status";
import type { ChainSigner, SignerStatus } from "../types";

interface SignatureListProps {
  /** The chain, already ordered by position then name (see the repository). */
  signers: ChainSigner[];
  /** Shown when Dokobit didn't report a name — the container's own sender. */
  fallbackName: string;
}

/** Soft circle per signer state, in the same family as SigningStatusBadge. */
const MARKER_CLASS: Record<SignerStatus, string> = {
  signed: "bg-success text-success-foreground",
  pending: "border border-dashed border-border bg-muted text-muted-foreground",
  rejected: "bg-destructive text-destructive-foreground",
  removed: "bg-muted text-muted-foreground",
};

const MARKER_ICON = {
  signed: Check,
  pending: Clock,
  rejected: X,
  removed: Minus,
} as const;

/**
 * The signer chain as an ordered stepper.
 *
 * Unlike the pre-chaining list this renders PENDING signers too — the point of
 * the page is to show who the chain is still waiting for. Signers sharing a
 * position are a parallel group: they may sign in any order, so they render as
 * several people inside ONE step, never as consecutive steps.
 *
 * Step numbers only appear when the chain actually has more than one position;
 * a single group — and every finished container, whose `document_signatures`
 * rows carry no recoverable order — renders as a flat list instead of claiming
 * an order that isn't real.
 */
export function SignatureList({ signers, fallbackName }: SignatureListProps) {
  const { t } = useLanguage();

  if (signers.length === 0) {
    return <p className="text-[11px] text-muted-foreground">{t.signing.chain.empty}</p>;
  }

  const steps = chainSteps(signers);
  const showSteps = isOrderedChain(signers);
  const current = currentPosition(signers);

  return (
    <>
      {/* §7's "Signing in order" — stated once, above the stepper, and only when
          the chain really is ordered. The step numbers alone don't say that the
          numbering is binding rather than decorative. */}
      {showSteps && (
        <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
          <ArrowDownNarrowWide className="h-3.5 w-3.5" aria-hidden="true" />
          {t.signing.chain.inOrder}
        </p>
      )}
      <ol className="flex flex-col gap-3">
        {steps.map((step) => (
          <li key={step.position} className="flex flex-col gap-2">
            {showSteps && (
              <div
                className={cn(
                  "text-[10px] font-bold uppercase tracking-wide",
                  step.position === current ? "text-primary" : "text-muted-foreground"
                )}
              >
                {t.signing.chain.step.replace("{position}", String(step.position))}
              </div>
            )}
            <ul className={cn("flex flex-col gap-3", showSteps && "pl-1")}>
              {step.signers.map((signer, index) => (
                <SignerRow
                  // `document_signatures` gives no stable per-row id here, and a
                  // parallel group may hold two people with the same name, so the
                  // key has to include the index within its step.
                  key={`${signer.position}-${signer.userId ?? "x"}-${index}`}
                  signer={signer}
                  fallbackName={fallbackName}
                />
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </>
  );
}

function SignerRow({ signer, fallbackName }: { signer: ChainSigner; fallbackName: string }) {
  const { t, language } = useLanguage();
  const Icon = MARKER_ICON[signer.status];
  const name = signer.name || fallbackName;

  const meta = [
    t.signing.chain.signerStatus[signer.status],
    signer.signedAt ? formatSigningDateTime(signer.signedAt, language) : null,
    signer.method ? t.signing.method[signingMethodKey(signer.method)] : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <li className="flex items-start gap-3">
      <span
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          MARKER_CLASS[signer.status]
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn("text-[13px] font-semibold", !name && "text-muted-foreground")}>
            {name || t.signing.panel.signerUnknown}
          </span>
          {signer.isYou && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              {t.signing.chain.you}
            </span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">{meta}</div>
      </div>
    </li>
  );
}
